import { collection, query, orderBy, limit, startAfter, where, getCountFromServer, getDocs } from "firebase/firestore";

/**
 * Build Firestore query with filters and pagination for World cups
 */
export function buildWorldCupsQuery(firestore, filters, pagination) {
    const { selectedTracks, sortOrder = 'newest' } = filters;
    const { cupsPerPage, lastVisible, pageNum } = pagination;
    
    const constraints = [orderBy("timestamp", sortOrder === 'oldest' ? 'asc' : 'desc')];
    
    // Add track filter - Firestore only allows ONE array-contains per query
    // If multiple tracks selected, we'll use array-contains-any and filter client-side
    if (selectedTracks && selectedTracks.length > 0) {
        const trackNames = selectedTracks.map(t => t.name);
        if (trackNames.length === 1) {
            constraints.push(where("trackEndList", "array-contains", trackNames[0]));
        } else {
            // For multiple tracks, we need to get cups with ANY of these tracks
            // and filter for ALL of them client-side in loadWorldCupsPage
            constraints.push(where("trackEndList", "array-contains-any", trackNames));
        }
    }
    
    // Add pagination - need more results when filtering multiple tracks client-side
    const limitCount = (selectedTracks && selectedTracks.length > 1) 
        ? cupsPerPage * 3  // Fetch more to account for client-side filtering
        : cupsPerPage;
    
    if (pageNum > 1 && lastVisible) {
        constraints.push(startAfter(lastVisible));
    }
    constraints.push(limit(limitCount));
    
    return query(collection(firestore, "worldCups"), ...constraints);
}

/**
 * Get total count of World cups matching filters
 */
export async function fetchWorldCupsCount(firestore, filters) {
    const { selectedTracks, sortOrder = 'newest' } = filters;
    
    try {
        const constraints = [];
        
        // Only add orderBy if we have other constraints, otherwise it's not needed for count
        if (selectedTracks && selectedTracks.length > 0) {
            constraints.push(orderBy("timestamp", sortOrder === 'oldest' ? 'asc' : 'desc'));
        }
        
        // For count, we can't do client-side filtering, so this will be approximate
        // when multiple tracks are selected (it counts cups with ANY of the tracks)
        if (selectedTracks && selectedTracks.length > 0) {
            const trackNames = selectedTracks.map(t => t.name);
            if (trackNames.length === 1) {
                constraints.push(where("trackEndList", "array-contains", trackNames[0]));
            } else {
                constraints.push(where("trackEndList", "array-contains-any", trackNames));
            }
        }
        
        const countQuery = constraints.length > 0 
            ? query(collection(firestore, "worldCups"), ...constraints)
            : collection(firestore, "worldCups");
            
        const snapshot = await getCountFromServer(countQuery);
        const count = snapshot.data().count;
        console.log("Fetched World cups count:", count);
        return count;
    } catch (error) {
        console.error("Error fetching World cups count:", error);
        // Fallback: try to get count without any constraints
        try {
            const fallbackSnapshot = await getCountFromServer(collection(firestore, "worldCups"));
            return fallbackSnapshot.data().count;
        } catch (fallbackError) {
            console.error("Fallback count also failed:", fallbackError);
            return 0;
        }
    }
}

/**
 * Load World cups from Firestore for a specific page
 */
export async function loadWorldCupsPage(firestore, filters, pagination) {
    try {
        const { selectedTracks } = filters;
        const { cupsPerPage } = pagination;
        
        const q = buildWorldCupsQuery(firestore, filters, pagination);
        const response = await getDocs(q);
        
        let loadedCups = [];
        response.forEach((doc) => {
            const docData = doc.data();
            loadedCups.push({ 
                id: doc.id, 
                ...docData, 
                tracks: docData.tracks // Keep raw format {start, end}
            });
        });

        const beforeFilterCount = loadedCups.length;
        
        // Client-side filtering for multiple track selection (AND logic)
        if (selectedTracks && selectedTracks.length > 1) {
            const trackNames = selectedTracks.map(t => t.name);
            loadedCups = loadedCups.filter(cup => {
                // Cup must contain ALL selected tracks
                return trackNames.every(trackName => cup.trackEndList.includes(trackName));
            });
        }
        
        const afterFilterCount = loadedCups.length;
        
        // Limit to requested page size after client-side filtering
        loadedCups = loadedCups.slice(0, cupsPerPage);

        const newLastVisible = response.docs.length > 0 
            ? response.docs[response.docs.length - 1] 
            : null;

        return {
            cups: loadedCups,
            lastVisible: newLastVisible,
            clientFiltered: selectedTracks && selectedTracks.length > 1,
            filteredCount: afterFilterCount,
            fetchedCount: beforeFilterCount
        };
    } catch (error) {
        console.error("Error loading World cups:", error);
        return {
            cups: [],
            lastVisible: null,
            clientFiltered: false,
            filteredCount: 0,
            fetchedCount: 0
        };
    }
}
